'use client';

import { CmsEditorialLink } from '@/cms/components/blocks/shared/cms-editorial-link';
import { CmsImageFrame } from '@/cms/components/blocks/shared/cms-image-frame';
import { CmsInlineRail } from '@/cms/components/blocks/shared/cms-inline-rail';
import { CmsSectionShell } from '@/cms/components/blocks/shared/cms-section-shell';
import { CmsSurface } from '@/cms/components/blocks/shared/cms-surface';
import { CmsTitleGroup } from '@/cms/components/blocks/shared/cms-title-group';
import { useEffect, useState } from 'react';
import type { ToolAppGalleryRenderModel } from './types';

function pickInitialCategoryKey(
  featuredKey: string,
  categories: ToolAppGalleryRenderModel['categories']
) {
  return categories.some((category) => category.key === featuredKey)
    ? featuredKey
    : (categories[0]?.key ?? '');
}

export function ToolAppGalleryBlockClient({
  model,
}: {
  model: ToolAppGalleryRenderModel;
}) {
  const [activeKey, setActiveKey] = useState(() =>
    pickInitialCategoryKey(model.featuredKey, model.categories)
  );
  const [slideDirection, setSlideDirection] = useState<
    'forward' | 'backward' | null
  >(null);

  useEffect(() => {
    setActiveKey(pickInitialCategoryKey(model.featuredKey, model.categories));
    setSlideDirection(null);
  }, [model.featuredKey, model.categories]);

  const visibleCards = model.cards.filter(
    (card) => card.categoryKey === activeKey
  );
  const renderedCards =
    visibleCards.length > 0
      ? visibleCards
      : model.cards.filter(
          (card) => card.categoryKey === (model.categories[0]?.key ?? '')
        );

  const activeCategoryIndex = model.categories.findIndex(
    (category) => category.key === activeKey
  );

  return (
    <CmsSectionShell
      width="default"
      spacing="default"
      className="cms-block-whats-more-whats-more-5-whats-more-5__section"
    >
      <CmsTitleGroup
        eyebrow={model.eyebrow}
        title={model.title}
        description={model.description}
        align="center"
        className="cms-block-whats-more-whats-more-5-whats-more-5__header"
      />

      <CmsInlineRail className="cms-block-whats-more-whats-more-5-whats-more-5__tabRail">
        <div
          className="cms-block-whats-more-whats-more-5-whats-more-5__tabList"
          role="tablist"
          aria-label={model.tabsAriaLabel}
        >
          {model.categories.map((category) => {
            const isActive = category.key === activeKey;
            const tabId = `${model.blockId}-tab-${category.key}`;
            const panelId = `${model.blockId}-panel-${category.key}`;

            return (
              <button
                key={tabId}
                id={tabId}
                type="button"
                role="tab"
                data-active={isActive ? 'true' : 'false'}
                aria-selected={isActive}
                aria-controls={panelId}
                className={`cms-block-whats-more-whats-more-5-whats-more-5__tabButton ${
                  isActive
                    ? 'cms-block-whats-more-whats-more-5-whats-more-5__tabButtonActive'
                    : ''
                }`}
                onClick={() => {
                  if (isActive) {
                    return;
                  }

                  const nextCategoryIndex = model.categories.findIndex(
                    (entry) => entry.key === category.key
                  );

                  setSlideDirection(
                    nextCategoryIndex >= activeCategoryIndex
                      ? 'forward'
                      : 'backward'
                  );
                  setActiveKey(category.key);
                }}
              >
                <span
                  className={`cms-block-whats-more-whats-more-5-whats-more-5__tabLabel ${
                    isActive
                      ? 'cms-block-whats-more-whats-more-5-whats-more-5__tabLabelActive'
                      : 'cms-block-whats-more-whats-more-5-whats-more-5__tabLabelInactive'
                  }`}
                >
                  {category.label}
                </span>
                <span
                  className={`cms-block-whats-more-whats-more-5-whats-more-5__tabUnderline ${
                    isActive
                      ? 'cms-block-whats-more-whats-more-5-whats-more-5__tabUnderlineActive'
                      : 'cms-block-whats-more-whats-more-5-whats-more-5__tabUnderlineInactive'
                  }`}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      </CmsInlineRail>

      <div
        id={`${model.blockId}-panel-${activeKey}`}
        role="tabpanel"
        aria-labelledby={`${model.blockId}-tab-${activeKey}`}
        className="cms-block-whats-more-whats-more-5-whats-more-5__panel"
      >
        <div
          key={activeKey}
          className={`cms-block-whats-more-whats-more-5-whats-more-5__grid ${
            slideDirection === 'forward'
              ? 'cms-block-whats-more-whats-more-5-whats-more-5__gridSlideForward'
              : slideDirection === 'backward'
                ? 'cms-block-whats-more-whats-more-5-whats-more-5__gridSlideBackward'
                : ''
          }`}
        >
          {renderedCards.map((card) => (
            <CmsEditorialLink
              key={`${model.blockId}-${activeKey}-${card.title}`}
              href={card.href}
              className="cms-block-whats-more-whats-more-5-whats-more-5__cardLink"
            >
              <CmsSurface
                as="article"
                variant="card"
                interactive
                className="cms-block-whats-more-whats-more-5-whats-more-5__card"
              >
                <CmsImageFrame
                  ratio="landscape"
                  className="cms-block-whats-more-whats-more-5-whats-more-5__media"
                >
                  <div
                    className="cms-block-whats-more-whats-more-5-whats-more-5__placeholderSurface"
                    aria-hidden="true"
                  />
                </CmsImageFrame>

                <div className="cms-block-whats-more-whats-more-5-whats-more-5__cardBody">
                  <h3 className="cms-body cms-block-whats-more-whats-more-5-whats-more-5__cardTitle">
                    {card.title}
                  </h3>
                </div>
              </CmsSurface>
            </CmsEditorialLink>
          ))}
        </div>
      </div>
    </CmsSectionShell>
  );
}
